import { influencer } from '@round/api';
import { Empty, VirtualizedTable, lucideIcons, tableHelpers } from '@round/creatorbase-ui';
import { CreatorCell } from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/cells/CreatorCell/CreatorCell';
import { LocationCell } from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/cells/LocationCell/LocationCell';
import {
    CostCell,
    FollowersCell,
} from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/cells/PlatformStatCells/PlatformStatCells';
import { TagsCell } from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/cells/TagsCell/TagsCell';
import { CellContext, Column, PAGE_SIZE } from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/helpers';
import { platformIcons, platformLabels } from 'Modules/Plans/helpers';
import { SelectAllCreatorsHeader, SelectCreatorCell } from './components/SelectCreatorCell/SelectCreatorCell';
import { Meta, getTableMeta } from './helpers';
import styles from './PoolCreatorsTable.module.css';

const { CircleAlert, FolderPlus, SearchX, UserCheck } = lucideIcons;

const ROW_HEIGHT_PX = 50;
const PLATFORM_STAT_COLUMN_SIZE = 115;

type Props = {
    data: influencer.CreatorSearchResult[];
    meta: Meta;
    onCreatorToggle: (accountId: number) => void;
    onSelectAll: (accountIds: number[]) => void;
    onClearSelection: () => void;
    haveCreatorsBeenAdded: boolean;
    hasPoolsError?: boolean;
    hasNoPools?: boolean;
    className?: string;
};

const PoolCreatorsTable = ({
    data,
    meta,
    onCreatorToggle,
    onSelectAll,
    onClearSelection,
    haveCreatorsBeenAdded,
    hasPoolsError,
    hasNoPools,
    className,
}: Props) => {
    const columns: Column[] = [
        {
            id: 'select',
            meta: { className: styles.selectColumn },
            header: (context) => (
                <SelectAllCreatorsHeader {...context} onSelectAll={onSelectAll} onClearSelection={onClearSelection} />
            ),
            cell: (context) => <SelectCreatorCell {...context} onToggle={onCreatorToggle} />,
            size: 70,
        },
        {
            header: 'Creator',
            id: 'creator',
            cell: CreatorCell,
            size: 200,
        },
        {
            header: 'Platform',
            id: 'platform',
            cell: PlatformCell,
            size: 140,
        },
        {
            header: 'Location',
            id: 'location',
            cell: LocationCell,
        },
        {
            header: 'Followers',
            id: 'total_follower_count',
            cell: FollowersCell,
            size: PLATFORM_STAT_COLUMN_SIZE,
        },
        {
            header: 'Cost',
            id: 'cost',
            cell: CostCell,
            size: PLATFORM_STAT_COLUMN_SIZE,
        },
        {
            header: 'Tags',
            id: 'tags',
            cell: TagsCell,
            size: 200,
        },
    ];

    return (
        <VirtualizedTable
            columns={columns}
            data={tableHelpers.getLoadingRows(data, { pageSize: PAGE_SIZE, isLoading: meta.isLoading })}
            meta={meta}
            virtualizerOptions={{
                estimateSize: () => ROW_HEIGHT_PX,
                getItemKey: (index) => `${index}-${data[index]?._id}` || `skeleton-${index}`,
                overscan: 50,
            }}
            containerClassName={className}
            noDataLabel={
                <PoolCreatorsEmptyState
                    hasError={meta.hasError}
                    haveCreatorsBeenAdded={haveCreatorsBeenAdded}
                    hasPoolsError={hasPoolsError}
                    hasNoPools={hasNoPools}
                />
            }
        />
    );
};

function PlatformCell({ table }: CellContext) {
    const { platform } = getTableMeta(table);

    if (!platform) {
        return null;
    }

    const Icon = platformIcons[platform];

    return (
        <span className={styles.platformCell}>
            <Icon className={styles.platformIcon} />
            {platformLabels[platform]}
        </span>
    );
}

type EmptyStateProps = {
    hasError?: boolean;
    haveCreatorsBeenAdded: boolean;
    hasPoolsError?: boolean;
    hasNoPools?: boolean;
};

function PoolCreatorsEmptyState(props: EmptyStateProps) {
    const { Icon, title, description } = getEmptyStateContent(props);

    return (
        <div className={styles.emptyState}>
            <Empty className={styles.empty}>
                <Empty.Media variant="icon">
                    <Icon />
                </Empty.Media>
                <Empty.Title>{title}</Empty.Title>
                <Empty.Description>{description}</Empty.Description>
            </Empty>
        </div>
    );
}

function getEmptyStateContent({ hasError, haveCreatorsBeenAdded, hasPoolsError, hasNoPools }: EmptyStateProps) {
    // Both pool-level states are reported here because the select itself only surfaces them inside
    // its dropdown, which the user has to open to see.
    if (hasPoolsError) {
        return {
            Icon: CircleAlert,
            title: 'Something went wrong',
            description: 'Could not load your pools. Please try again.',
        };
    }

    if (hasNoPools) {
        return {
            Icon: FolderPlus,
            title: 'No creator pools',
            description: 'Create a pool to add creators from it.',
        };
    }

    if (hasError) {
        return {
            Icon: CircleAlert,
            title: 'Something went wrong',
            description: "Could not load this pool's creators. Please try again.",
        };
    }

    if (haveCreatorsBeenAdded) {
        return {
            Icon: UserCheck,
            title: 'No creators left',
            description: 'Everyone still listed has been added. Try another pool.',
        };
    }

    return {
        Icon: SearchX,
        title: 'No creators found',
        description: "No creators in this pool on this campaign's platform.",
    };
}

export default PoolCreatorsTable;
